/* eslint-disable import/prefer-default-export */
import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import { getOrderData } from '@api/returns'
import { getLink, prefixLocale } from '@helpers/env'
import trackError from '@helpers/trackError'
import Translations from '@helpers/translations'
import { identify } from '@helpers/analytics'
import { getCustomerFit } from '@api/fit'
import { objectKeys } from '@helpers/utils'
import { ReturnsOrder } from '@api/returns/types'
import { EmptyObject } from 'types/helpers'
import { Fit } from 'types/fit'
import { NotificationProps } from '@components/ui/Notification'

type GetNotificationMessageReturn =
  | {
      type: NotificationProps['type']
      content: NotificationProps['content']
    }
  | EmptyObject

function getNotificationMessage({
  orderData
}: {
  orderData: ReturnsOrder
}): GetNotificationMessageReturn {
  if (objectKeys(orderData).length) {
    const includesNonReturnItems = orderData.products.filter(
      props => props.isReturnable === false
    )

    const returnsWithNoCarrier =
      orderData.returns &&
      Object.values(orderData.returns).filter(
        product => product?.carrier_id === null
      )

    switch (true) {
      case returnsWithNoCarrier.length > 0:
        return {
          type: 'warning',
          content: Translations.localise(
            'pages.returns.returns-notifications.carrier-selection'
          )
        }
      case orderData.products && !orderData.products.length:
        return {
          type: 'warning',
          content: Translations.localise(
            'pages.returns.returns-notifications.all-items-returned'
          )
        }
      case orderData.expired:
        return {
          type: 'error',
          content: (
            <span>
              This order was placed over 200 days ago and is now outside our
              returns window. If you have any questions please{' '}
              <b>
                <a
                  style={{ color: '#DC1E32' }}
                  href={getLink('/pages/contact-us')}
                >
                  get in touch
                </a>
              </b>
            </span>
          )
        }
      case includesNonReturnItems.length > 0:
        return {
          type: 'warning',
          content: (
            <span>
              You are unable to return one or more items in this order{' '}
              <b>
                <a
                  style={{ color: '#F28F1C' }}
                  href={getLink('/pages/contact-us')}
                >
                  get in touch
                </a>
              </b>
            </span>
          )
        }
      default:
        return {}
    }
  }
  return {}
}

export type OrderData = ReturnsOrder & {
  customerFit?: Fit
}

type UseFetchOrderDataProps = {
  orderNumber: string
  postcode: string
  onSuccess: (orderData: OrderData) => void
  setNotificationMessage: (args: GetNotificationMessageReturn) => void
}

export default function useFetchOrderData({
  orderNumber,
  postcode,
  onSuccess,
  setNotificationMessage
}: UseFetchOrderDataProps) {
  const router = useRouter()

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const order = await getOrderData({
          order_name: orderNumber,
          postcode,
          admin: router.query.admin === 'true'
        })

        const userId = order.customer?.id

        const notification = getNotificationMessage({
          orderData: order
        })

        if (notification) {
          setNotificationMessage(notification)
        }

        const { fit } = await getCustomerFit({ customerId: String(userId) })
        const customerFit =
          fit.top && fit.bottom ? { ...fit.top, ...fit.bottom } : undefined

        onSuccess({ ...order, customerFit })

        identify(userId, {
          email: order.customer?.email,
          category: 'Returns portal'
        })
      } catch (e) {
        const error = e as Error
        trackError(new Error('fetchOrderData failed'), { error })
        router.push(
          `/pages?pagesHandle=returns&error=true`,
          `${prefixLocale()}/pages/returns?error=true`,
          {
            shallow: true
          }
        )
      }
    }
    fetchOrderData()
  }, [])
}
